import {useRef} from 'react';

import {Condition, ConditionGroup, SurveyValues} from 'common/models/survey';
import {isNil} from 'lodash';
import checkCondition from 'ui/utils/checkCondition';
import checkConditionGroups from 'ui/utils/checkConditionGroups';
import getSeriesQuestionSlug from 'ui/utils/getSeriesQuestionSlug';

interface MatchConditionsParams {
    conditions?: ConditionGroup[];
    seriesName?: string;
    index?: number;
    question?: string;
}
export interface MatchConditions {
    (params: MatchConditionsParams): boolean;
}

const getCheckConditionFunc =
    (data: SurveyValues, hiddenQuestions: Set<string>, seriesName?: string, index?: number) =>
    ({question, condition, value}: Condition) => {
        if (!question) {
            return true;
        }

        let questionSlug = question;
        let watchValue = data[question];
        if (
            isNil(watchValue) &&
            seriesName &&
            index !== undefined &&
            Array.isArray(data[seriesName])
        ) {
            watchValue = (data[seriesName] as [])?.[index]?.[question];
            questionSlug = getSeriesQuestionSlug(seriesName, index, question);
        }

        if (hiddenQuestions.has(questionSlug)) {
            return false;
        }

        return checkCondition(condition, watchValue, value);
    };

const useConditions = (data: SurveyValues): MatchConditions => {
    const hiddenQuestions = useRef<Set<string>>(new Set());

    const match: MatchConditions = ({conditions, seriesName, index, question}) => {
        if (Object.keys(data).length === 0 || !conditions) {
            return true;
        }

        const visible = checkConditionGroups(
            conditions,
            getCheckConditionFunc(data, hiddenQuestions.current, seriesName, index),
        );
        if (question) {
            let questionSlug = question;
            if (seriesName && !isNil(index)) {
                questionSlug = getSeriesQuestionSlug(seriesName, index, question);
            }

            if (visible) {
                hiddenQuestions.current.delete(questionSlug);
            } else {
                hiddenQuestions.current.add(questionSlug);
            }
        }
        return visible;
    };
    return match;
};
export default useConditions;
